import ReactGA from 'react-ga4';
import { observable, action } from 'mobx';

// FIXME - import the proxied script differently
// in order to avoid loader issues and also it being part of the bundle
// eslint-disable-next-line import/order, import/extensions
import GAproxied from '../../assets/scripts/ga-proxied.js';

import {
  inWhitelabel,
  getWhitelabelConfig,
  resolveColor,
} from 'core/whitelabel';

import { createHash } from 'crypto';

import { hasSpaceFreeTrial } from 'utils/freeTrials';

import { conf as globalConf } from 'apps/config.yaml';

let site = globalConf.siteName;
if (inWhitelabel()) {
  const conf = getWhitelabelConfig();
  site = conf.title || 'Whitelabel';
}

const getIntercomBasic = () => ({
  app_id: INTERCOM_APP_ID,
  action_color: resolveColor('main'),
  background_color: resolveColor('main'),
});

const defaultFBEvents = [
  'AddPaymentInfo',
  'AddToCart',
  'AddToWishlist',
  'CompleteRegistration',
  'Contact',
  'Donate',
  'FindLocation',
  'InitiateCheckout',
  'Lead',
  'PageView',
  'Purchase',
  'Schedule',
  'Search',
  'StartTrial',
  'SubmitApplication',
  'Subscribe',
  'ViewContent',
];

const gaContentGroup = {
  site: 'contentGroup1',
  freeTrial: 'contentGroup2',
  intent: 'contentGroup3',
  userType: 'contentGroup4',
};

class Analytics {
  @observable enabled = false;
  @observable intercomVisible = false;

  profile = null;
  constructor() {
    this.gaInitialized = false;
    try {
      this.debugEnabled = localStorage.getItem('debug') || false;
      const LSpolicy =
        JSON.parse(localStorage.getItem('acceptedPolicy')) || false;
      this.enabled = LSpolicy;
    } catch (e) {
      this.enabled = false;
    }
    localStorage.setItem('acceptedPolicy', JSON.stringify(this.enabled));
    this.intercomVisible = !!INTERCOM_APP_ID;
  }

  @action Enable = userId => {
    this.enabled = true;
    localStorage.setItem('acceptedPolicy', JSON.stringify(this.enabled));
    this.initialize(userId);
  };

  initialize(profile) {
    const userId = profile?.username;
    if (GOOGLE_ANALYTICS_ID && GA4_TRACKING_ID) {
      const gaSettings = { titleCase: false };
      if (GOOGLE_ANALYTICS_PROXIED) {
        gaSettings.gaAddress = GAproxied;
      }
      // Use both trackers for continuity.
      // They indicated us to keep the old script
      ReactGA.initialize([
        { trackingId: GOOGLE_ANALYTICS_ID, gaOptions: gaSettings },
        { trackingId: GA4_TRACKING_ID },
      ]);
      ReactGA.set(gaContentGroup.site, site);
      const intent = profile?.intent?.name;
      const userType = profile?.userType?.name;
      if (intent) {
        ReactGA.set(gaContentGroup.intent, intent);
      }
      if (userType) {
        ReactGA.set(gaContentGroup.userType, userType);
      }
      this.gaInitialized = true;
    }

    if (window.Sentry) {
      window.Sentry.configureScope(scope => {
        scope.setTag('project', 'alpha');
        scope.setTag('service', 'frontend');
      });
    }

    if (this.enabled && INTERCOM_APP_ID && !window.Intercom) {
      if (!this.intercomVisible) {
        try {
          // FIXME - hack to clear "openOnBoot" field from Intercom's localStorage entry
          // as it can't be set through the initializing parameters and can't call
          // Intercom('hide') API before initializing (it would be possible after
          // calling initializeIntercom, but it would "pop" for almost a second
          // before disappearing).
          const intercomKey = `intercom.intercom-state-${INTERCOM_APP_ID}`;
          const intercomObject = JSON.parse(localStorage.getItem(intercomKey));
          intercomObject.openOnBoot = { type: null, metadata: {} };
          localStorage.setItem(intercomKey, JSON.stringify(intercomObject));
        } catch (e) {
          // It is normal behaviour not finding the key after a clean start
        }
      }

      window.initializeIntercom({
        ...getIntercomBasic(),
        hide_default_launcher: !this.intercomVisible,
      });
    }

    if (userId) {
      this.linkUser(profile);
    }
  }

  linkUser(profile) {
    if (!profile) {
      this.logError(
        new Error('Attempted to linkUser when no profile was provided'),
      );
      return;
    }

    this.profile = profile;

    const userId = profile.username;
    if (GOOGLE_ANALYTICS_ID) {
      ReactGA.set({ userId });
      ReactGA.set(
        gaContentGroup.freeTrial,
        !!hasSpaceFreeTrial(profile.inSpace),
      );
    }

    if (INTERCOM_APP_ID && window.Intercom) {
      this.verifyIntercom()
        .then(user_hash => {
          window.Intercom('boot', {
            ...getIntercomBasic(),
            app_id: INTERCOM_APP_ID,
            user_id: userId,
            user_hash,
            created_at: Date.now(),
          });
        })
        .catch(error => {
          this.logError(error);
        });
    }

    if (window.Sentry) {
      window.Sentry.configureScope(scope => {
        scope.setUser({ username: userId.replace('@', '') });
      });
    }

    if (MODE === 'development') {
      // eslint-disable-next-line no-console
      console.log(`[analytics] linking user: ${userId}`);
    }
  }

  sendFbConversionEvent(event) {
    if (FACEBOOK_PIXEL_ID && FACEBOOK_CONVERSIONS_ACCESS_TOKEN) {
      const API_VERSION = 'v12.0';
      const xhr = new XMLHttpRequest();
      xhr.open(
        'POST',
        `https://graph.facebook.com/${API_VERSION}/${FACEBOOK_PIXEL_ID}/events?access_token=${FACEBOOK_CONVERSIONS_ACCESS_TOKEN}`,
        true,
      );
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.send(JSON.stringify(event));
    }
  }

  sendEvent(
    eventName,
    eventData = {},
    sendTo = { ga: true, intercom: true, facebook: true, twitter: false },
  ) {
    let patchedEventData = { ...eventData };
    let patchedEventName = eventName;
    if (sendTo.ga && GOOGLE_ANALYTICS_ID && GA4_TRACKING_ID) {
      if (!this.gaInitialized) {
        return;
      }

      switch (patchedEventName) {
        case 'modal-view':
          ReactGA.send({ hitType: 'pageview', page: patchedEventData.name });
          break;
        case 'page-view':
          ReactGA.send({ hitType: 'pageview', page: patchedEventData.url });
          break;
        default:
          ReactGA.event(patchedEventName, eventData);
          break;
      }
    }
    if (sendTo.intercom && window.Intercom) {
      window.Intercom('trackEvent', patchedEventName, patchedEventData);
    }
    patchedEventName =
      patchedEventName === 'page-view' ? 'PageView' : patchedEventName;
    if (sendTo.twitter && window.twq) {
      window.twq('track', patchedEventName, patchedEventData);
    }
    if (sendTo.facebook) {
      patchedEventData = {
        ...eventData,
        content_type: 'product',
        contents: [
          { ...this.profile?.userType },
          { ...this.profile?.intent },
        ].filter(e => !!e?.id),
      };

      if (window.fbq) {
        // For pixel legacy
        if (defaultFBEvents.includes(patchedEventName)) {
          window.fbq('track', patchedEventName, patchedEventData);
        } else {
          // Issue "CustomizeProduct" event on custom events
          patchedEventData.content_name = patchedEventName;

          patchedEventData.currency = patchedEventData.currency
            ? patchedEventData.currency
            : 'EUR';
          patchedEventData.value = patchedEventData.value
            ? patchedEventData.value
            : 2;

          window.fbq('track', 'CustomizeProduct', patchedEventData);
        }
      }

      const eventsForConvAPIFilter = [
        'CompleteRegistration',
        'SubmitApplication',
        'Schedule',
        'Donate',
        'Contact',
        'Lead',
      ];

      // Don't send the event to Conversions API if it is not a desired one
      if (eventsForConvAPIFilter.includes(patchedEventName)) {
        const now = new Date();
        const utc = new Date(now.getTime() + now.getTimezoneOffset() * 60000);

        if (this.profile) {
          const mailHash = createHash('sha256')
            .update(this.profile.user.email)
            .digest('hex');

          // Move the currency and value to inside the contents, so it is later sent as custom_data
          if (patchedEventData.currency) {
            patchedEventData.contents.push({
              currency: patchedEventData.currency,
            });
          }
          if (patchedEventData.value) {
            patchedEventData.contents.push({ value: patchedEventData.value });
          }

          const contentsAsObj = {};

          for (let i = 0; i < patchedEventData.contents.length; i += 1) {
            const entry = patchedEventData.contents[i];

            const keys = Object.keys(entry);
            for (let k = 0; k < keys.length; k += 1) {
              contentsAsObj[keys[k]] = entry[keys[k]];
            }
          }

          const conversionEvent = {
            data: [
              {
                event_name: patchedEventName,
                action_source: 'website',
                event_time: utc,
                custom_data: contentsAsObj,
                user_data: { em: [mailHash] },
              },
            ],
          };
          this.sendFbConversionEvent(conversionEvent);
        }
      }
    }

    // Send page-view events to our own server
    if (eventName === 'page-view') {
      fetch(
        `${BACKEND_URL_BASE}/stats/pageview?uip=browser&dp=${window.location.href}`,
      )
        .then(response => response)
        .then(() => {});
    }

    // Send custom events to Facebook anyways
    if (MODE === 'development') {
      const services = Object.keys(sendTo).filter(service => sendTo[service]);
      // eslint-disable-next-line no-console
      console.log(
        `[analytics] sending event ${patchedEventName}, metadata: ${JSON.stringify(
          patchedEventData,
        )}, services: ${services}`,
      );
    }
  }

  linkedinPixel(conversionID) {
    const partnerID = window._linkedin_partner_id;
    if (!(document && partnerID && conversionID)) return;
    const container = document.querySelector('body');
    const pixel = document.createElement('img');
    pixel.src = `https://px.ads.linkedin.com/collect/?pid=${partnerID}&conversionId=${conversionID}&fmt=gif`;
    pixel.onload = () => {
      try {
        container.removeChild(pixel);
      } catch (err) {
        console.error(`Pixel ${conversionID} couldn't be removed`);
      }
    };
    container.appendChild(pixel);
  }

  logError(exception, scopingFunction = () => {}) {
    if (window.Sentry) {
      window.Sentry.withScope(scope => {
        scopingFunction(scope);
        window.Sentry.captureException(exception);
      });
    } else {
      console.warn('no sentry');
    }
    console.error(exception);
  }

  addBreadcrumb(breadcrumb) {
    if (window.Sentry) {
      window.Sentry.addBreadcrumb(breadcrumb);
    }
  }

  logDebug(message) {
    if (this.debugEnabled) {
      // eslint-disable-next-line no-console
      console.log(`[***][DEBUG] ${message}`);
    }
  }

  unlinkUser() {
    this.profile = null;
    if (GOOGLE_ANALYTICS_ID) ReactGA.set({ userId: null });
    if (window.Intercom) window.Intercom('shutdown');
    if (window.Sentry) {
      window.Sentry.configureScope(scope => {
        scope.setUser();
      });
    }

    if (MODE === 'development') {
      // eslint-disable-next-line no-console
      console.log(`[analytics] unlinking user`);
    }
  }

  verifyIntercom() {
    return fetch(`${API_URL_BASE}/intercom/verify`, {
      credentials: 'include',
    }).then(response => {
      if (!response.ok) {
        throw Error(response.statusText);
      }
      return response.text();
    });
  }

  setIntercomVisible = visible => {
    if (this.intercomVisible === visible) return;

    if (window.Intercom) {
      window.Intercom('update', {
        hide_default_launcher: !visible,
      });
      if (!visible) {
        window.Intercom('hide');
      }
    }

    this.intercomVisible = visible;
  };
}

export const analytics = new Analytics();
